import { useMemo, useState } from "react";
import { ArrowLeft, Loader2, Mail, MessageSquare } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Card, CardContent } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { cn } from "../../../../lib/utils";
import SignalFilterContainer from "../../signals/signal-overview/components/SignalFilterContainer";
import useObjectSearchTable from "../../../../shared/table/useObjectSearchTable";
import { useWorkspaceObjectSchema } from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import type { AlertDraft } from "./AlertsAssignmentPage";
import type { VariableOption } from "./VariableChipEditor";
import SlackActionCard from "./SlackActionCard";
import EmailActionCard from "./EmailActionCard";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SUPPRESSION_OPTIONS = [
  { value: "every_occurrence", label: "Every time", description: "Alert every time an account matches, on every run" },
  { value: "every_24_hours", label: "Every 24 hours", description: "At most once per day per account" },
  { value: "every_48_hours", label: "Every 48 hours", description: "At most once every 2 days per account" },
  { value: "every_72_hours", label: "Every 72 hours", description: "At most once every 3 days per account" },
  { value: "weekly", label: "Weekly", description: "At most once per week per account" },
  { value: "biweekly", label: "Biweekly", description: "At most once every 2 weeks per account" },
  { value: "monthly", label: "Monthly", description: "At most once per month per account" },
  { value: "quarterly", label: "Quarterly", description: "At most once per quarter per account" },
  { value: "yearly", label: "Yearly", description: "At most once per year per account" },
  { value: "first_time_only", label: "First time only", description: "Only alert the very first time an account matches, never again" },
];

const DEFAULT_PREVIEW_FIELDS = ["accounts.name", "accounts.external_id", "accounts.last_seen"];

/* ------------------------------------------------------------------ */
/*  Account Preview                                                    */
/* ------------------------------------------------------------------ */

const AccountPreview = ({ workspaceId, filter }: { workspaceId: string; filter: any }) => {
  const hasPredicates = filter?.predicates?.some(
    (group: any) => group?.predicates?.some((p: any) => p?.field?.trim()),
  );

  const { rows, total, isLoading, isFetching } = useObjectSearchTable({
    workspaceId,
    entityType: "accounts",
    enabled: !!workspaceId && hasPredicates,
    initialPageSize: 10,
    initialSort: { field: "accounts.last_seen", order: "desc" },
    queryOptions: {
      filter: hasPredicates ? filter : undefined,
      selectedFields: DEFAULT_PREVIEW_FIELDS,
      fallbackSortField: "accounts.last_seen",
    },
    parseRows: (rawRows) =>
      (rawRows || []).map((record: any) => {
        const fields = record?.fields || {};
        return {
          name: fields.name || record?.name || "",
          external_id: fields.external_id || record?.id || "",
          last_seen: fields.last_seen || "",
        };
      }),
    resetPageDeps: [JSON.stringify(filter)],
  });

  if (!hasPredicates) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-center text-sm text-muted-foreground">
          Add filter conditions to preview matching accounts.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading preview...
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-medium">Matching Accounts</h3>
        <Badge
          variant="outline"
          className={cn(
            "rounded-full px-2 text-[11px]",
            total === 0 && "border-amber-300 text-amber-700",
          )}
        >
          {isFetching ? "..." : total}
        </Badge>
      </div>
      {total === 0 ? (
        <p className="text-xs text-amber-600">No accounts match these conditions.</p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border text-xs">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">External ID</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any, idx: number) => (
                <tr key={idx} className="border-b border-border last:border-0">
                  <td className="px-3 py-1.5 text-foreground">{row?.name || "\u2014"}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{row?.external_id || "\u2014"}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {row?.last_seen ? new Date(row.last_seen).toLocaleDateString() : "\u2014"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

type Props = {
  draft: AlertDraft;
  saving: boolean;
  onSave: (d: AlertDraft) => void;
  onCancel: () => void;
};

const AlertEditor = ({ draft: initial, saving, onSave, onCancel }: Props) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id ?? "";
  const [draft, setDraft] = useState<AlertDraft>({ ...initial });

  const patch = (partial: Partial<AlertDraft>) => setDraft((d) => ({ ...d, ...partial }));

  /* --- workspace schema for variable chips --- */
  const workspaceSchema: any = useWorkspaceObjectSchema({
    workspaceId,
    enabled: !!workspaceId,
  } as any);

  const variables: VariableOption[] = useMemo(() => {
    const schema = workspaceSchema?.data;
    if (!schema) return [];
    const vars: VariableOption[] = [];
    const accountFields = schema.fieldsByObjectSlug?.accounts?.list || [];
    accountFields
      .filter((f: any) => !f.exclude_display)
      .forEach((f: any) => {
        vars.push({
          name: f.name,
          display_name: f.display_name || f.name,
          group: "Account Traits",
        });
      });
    const accountMetrics = schema.metricsByObjectSlug?.accounts?.list || schema.metrics?.list || [];
    accountMetrics
      .filter((m: any) => !m.exclude_display)
      .forEach((m: any) => {
        vars.push({
          name: m.name,
          display_name: m.display_name || m.name,
          group: "Account Metrics",
        });
      });
    return vars;
  }, [workspaceSchema?.data]);

  /* --- filter helpers --- */
  const handleFilter = (
    field: string,
    operator: string,
    value: any,
    type: string,
    entityType: string,
    id: number,
    filterId: number,
  ) => {
    const newFilters = { ...draft.filters };
    const predicates = [...(newFilters.predicates || [])];
    if (!predicates[filterId]) predicates[filterId] = { predicates: [] };
    const group = { ...predicates[filterId] };
    const items = [...(group.predicates || [])];
    items[id] = { field, operator, value, type, entity_type: entityType };
    group.predicates = items;
    predicates[filterId] = group;
    newFilters.predicates = predicates;
    patch({ filters: newFilters });
  };

  const handleRemoveFilter = (id: number, filterId?: number) => {
    const fid = filterId ?? 0;
    const newFilters = { ...draft.filters };
    const predicates = [...(newFilters.predicates || [])];
    if (predicates[fid]) {
      const group = { ...predicates[fid] };
      const items = [...(group.predicates || [])];
      items.splice(id, 1);
      group.predicates = items;
      predicates[fid] = group;
    }
    newFilters.predicates = predicates;
    patch({ filters: newFilters });
  };

  const handleAddFilter = (filterId?: number) => {
    const fid = filterId ?? 0;
    const newFilters = { ...draft.filters };
    const predicates = [...(newFilters.predicates || [])];
    if (!predicates[fid]) predicates[fid] = { predicates: [] };
    const group = { ...predicates[fid] };
    group.predicates = [
      ...(group.predicates || []),
      { field: "", operator: "", value: "", type: "", entity_type: "column" },
    ];
    predicates[fid] = group;
    newFilters.predicates = predicates;
    patch({ filters: newFilters });
  };

  /* --- action helpers --- */
  const addSlackAction = () =>
    patch({ actions: [...draft.actions, { type: "send_to_slack", channel_id: "", message: "" }] });

  const addEmailAction = () =>
    patch({ actions: [...draft.actions, { type: "send_to_email", to: "", subject: "", body: "" }] });

  const updateAction = (idx: number, partial: any) => {
    const actions = [...draft.actions];
    actions[idx] = { ...actions[idx], ...partial };
    patch({ actions });
  };

  const removeAction = (idx: number) => {
    const actions = [...draft.actions];
    actions.splice(idx, 1);
    patch({ actions });
  };

  /* --- selected suppression option --- */
  const selectedSuppression = SUPPRESSION_OPTIONS.find((o) => o.value === draft.suppression_period);

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">{initial.id ? "Edit Alert" : "New Alert"}</h2>
      </div>

      {/* Two-column layout: form left, preview right */}
      <div className="flex gap-8">
        {/* ---- Left: Form ---- */}
        <div className="flex-1 min-w-[420px] space-y-6">
          {/* Name & Description */}
          <div className="space-y-3">
            <div>
              <Label>Alert Name</Label>
              <Input
                value={draft.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="e.g. Churn risk notification"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={draft.description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder="Describe what this alert does"
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          {/* Filter Conditions */}
          <div>
            <Label className="mb-2 block">Filter Conditions</Label>
            <p className="mb-3 text-xs text-muted-foreground">
              Define which accounts trigger this alert. Runs every 6 hours automatically.
            </p>
            <Card>
              <CardContent className="pt-4">
                {(draft.filters?.predicates || []).map((group: any, filterId: number) => (
                  <SignalFilterContainer
                    key={filterId}
                    selectedFilters={group.predicates}
                    handleFilter={handleFilter}
                    handleRemove={handleRemoveFilter}
                    handleAdd={handleAddFilter}
                    filterId={filterId}
                    entityRelation={draft.entity_relation}
                  />
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Reentry Policy */}
          <div>
            <Label>Reentry Policy</Label>
            <p className="mb-2 text-xs text-muted-foreground">
              Controls how often an account that continues to match can re-trigger this alert.
              For example, if set to "Weekly", an account that matched on Monday won&apos;t trigger another alert until the following Monday, even if it still matches on every run.
            </p>
            <Select value={draft.suppression_period} onValueChange={(v) => patch({ suppression_period: v })}>
              <SelectTrigger className="w-[280px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPRESSION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span>{opt.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedSuppression && (
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                {selectedSuppression.description}
              </p>
            )}
          </div>

          {/* Notification Actions */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div>
                <Label>Notification Actions</Label>
                <p className="text-xs text-muted-foreground">Where should alerts be sent?</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={addSlackAction}>
                  <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                  Add Slack
                </Button>
                <Button variant="outline" size="sm" onClick={addEmailAction}>
                  <Mail className="mr-1.5 h-3.5 w-3.5" />
                  Add Email
                </Button>
              </div>
            </div>

            {draft.actions.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  No actions added yet. Add a Slack or Email notification above.
                </CardContent>
              </Card>
            )}

            <div className="space-y-3">
              {draft.actions.map((action, idx) =>
                action.type === "send_to_slack" ? (
                  <SlackActionCard
                    key={idx}
                    action={action as any}
                    onChange={(partial) => updateAction(idx, partial)}
                    onRemove={() => removeAction(idx)}
                    variables={variables}
                  />
                ) : (
                  <EmailActionCard
                    key={idx}
                    action={action as any}
                    onChange={(partial) => updateAction(idx, partial)}
                    onRemove={() => removeAction(idx)}
                    variables={variables}
                  />
                ),
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t pt-4">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button onClick={() => onSave(draft)} disabled={saving}>
              {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {initial.id ? "Save Changes" : "Create Alert"}
            </Button>
          </div>
        </div>

        {/* ---- Right: Preview ---- */}
        <div className="w-[380px] shrink-0">
          <div className="sticky top-8 rounded-lg border border-border bg-background">
            <div className="border-b border-border px-4 py-3">
              <h3 className="text-sm font-medium">Account Preview</h3>
              <p className="text-[11px] text-muted-foreground">Accounts matching your filter conditions</p>
            </div>
            <AccountPreview workspaceId={workspaceId} filter={draft.filters} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertEditor;
